/**
 * `useFetch` — a tiny hook that drives the four UI states the dashboard
 * shell promises on every route: idle, loading, ready (with an empty
 * sub-state), and error.
 *
 * Components never call the API client directly — they pass an async
 * callback to this hook so that error / loading semantics are uniform.
 */

import { useEffect, useState } from 'react';
import type { Outcome, ApiError } from './types';

export type FetchState<T> =
  | { status: 'loading'; data: null; error: null }
  | { status: 'ready'; data: T; error: null }
  | { status: 'error'; data: null; error: ApiError };

/**
 * Run `task` once whenever any of `deps` changes. Resolves to a stable
 * `FetchState<T>` shape that components can switch on.
 *
 * The `task` callback is called fresh every effect tick — callers should
 * keep it cheap or memoise via the `deps` array.
 */
export function useFetch<T>(
  task: () => Promise<Outcome<T>>,
  deps: ReadonlyArray<unknown>,
): FetchState<T> {
  const [state, setState] = useState<FetchState<T>>({
    status: 'loading',
    data: null,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState({ status: 'loading', data: null, error: null });
    task().then(
      (outcome) => {
        if (cancelled) return;
        if (outcome.ok) {
          setState({ status: 'ready', data: outcome.value, error: null });
        } else {
          setState({ status: 'error', data: null, error: outcome.error });
        }
      },
      (err) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : 'unknown error';
        setState({
          status: 'error',
          data: null,
          error: { code: 'unhandled', message },
        });
      },
    );
    return () => {
      cancelled = true;
    };
  }, deps);

  return state;
}
